/**
 * Whole counts, formatted for the language the operator chose.
 *
 * `Number.prototype.toLocaleString()` with no argument formats in the BROWSER's
 * locale — whatever the operating system is set to — which has nothing to do
 * with the language picked in the app. The same reasoning §3.9 applies to dates:
 * the figure must follow the screen it is on, not the machine it is read on.
 *
 * Digits stay Latin. Burmese CLDR defaults to Myanmar digits, while the ids and
 * the durations beside these cells (`formatDurationShort` builds `${n} ${unit}`)
 * are Latin in every language — a single column in another script is a figure an
 * operator cannot line up against the ones next to it.
 */

/** Built formatters are reused: a table builds one per cell otherwise. */
const formatters = new Map<string, Intl.NumberFormat>();

function formatterFor(lang: string): Intl.NumberFormat {
  const cached = formatters.get(lang);
  if (cached) return cached;
  let built: Intl.NumberFormat;
  try {
    built = new Intl.NumberFormat(lang || 'en', {
      numberingSystem: 'latn',
      maximumFractionDigits: 0,
    });
  } catch {
    // A malformed language tag is a RangeError, and a table cell is not a place
    // to take the page down over one.
    built = new Intl.NumberFormat('en', { numberingSystem: 'latn', maximumFractionDigits: 0 });
  }
  formatters.set(lang, built);
  return built;
}

/**
 * `value` as a grouped whole number in `lang`, or `'-'` when there is no number
 * — an unread count is not a count of zero.
 */
export function formatCount(value: number | null | undefined, lang: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  return formatterFor(lang).format(value);
}
